import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { DailyService } from '@app/core';
import { TypeFile } from '@app/core/models';
import { CourseService } from '@app/core/services/course.service';
import { ModalFileViewerComponent } from '@app/modules/eclass/components/modal-file-viewer/modal-file-viewer.component';
import { LearningTrailsModule } from '@app/modules/learning-trails/learning-trails.module';
import { SHARED_MODULES } from '@app/shared';
import { safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { findIndex, first } from 'lodash';
import { filter, Observable, switchMap, take } from 'rxjs';

interface ModalTrailStudentData {
  trailElements: any[];
  dailyId?: number | string;
}

@Component({
  imports: [SHARED_MODULES, LearningTrailsModule],
  templateUrl: './modal-trail-student.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalTrailStudentComponent {
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _dialog = inject(Dialog);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _cdrf = inject(ChangeDetectorRef);
  private readonly _dailyService = inject(DailyService);
  private readonly _courseService = inject(CourseService);
  public readonly data = inject<ModalTrailStudentData>(DIALOG_DATA);
  public currentElement: any;
  public multipleElements: boolean = false;
  public elementData$: Observable<any>;

  constructor() {
    this.multipleElements = this.data.trailElements.length > 1;
    this.setContent(first(this.data.trailElements));
  }

  public get isFirst(): boolean {
    const currentIndex = findIndex(this.data.trailElements, (element) => element.id === this.currentElement.id);
    return currentIndex === 0;
  }

  public get isLast(): boolean {
    const currentIndex = findIndex(this.data.trailElements, (element) => element.id === this.currentElement.id);
    return currentIndex === this.data.trailElements.length - 1;
  }

  public isSelected(elementId): boolean {
    return elementId === this.currentElement.id;
  }

  public onCloseModal() {
    this._dialogRef.close({ trail_elements: this.data.trailElements });
  }

  public setContent(element: any) {
    if (this.currentElement?.id === element.id) {
      return;
    }

    this.currentElement = element;
    const getElementData = element.evento_id ? this.getElementData : this.getElementDataWithoutEvent;
    getElementData.call(this, element);

    if (!element.completed) {
      this.markElementAsRead(element, !!element.evento_id);
    }
  }

  public mapTypeToIcon(element: string): string {
    const type = element.toLowerCase();

    const typeMapping: Record<string, string> = {
      audio: 'icon-headphone.svg',
      avaliacao: 'icon-book-pencil.svg',
      flip: 'icon-books.svg',
      imagem: 'icon-landscape.svg',
      link: 'icon-clip.svg',
      texto: 'icon-text-lines.svg',
      video: 'icon-film-alt.svg',
    };

    return typeMapping[type] || 'icon-help.svg';
  }

  public getStyleTextIcon(type: string): string {
    switch (type) {
      case 'avaliacao':
        return 'text-base-trilhas-tipo-quiz';
      case 'imagem':
        return 'text-base-trilhas-tipo-image';
      case 'flip':
        return 'text-base-trilhas-tipo-ebook';
      case 'texto':
        return 'text-base-trilhas-tipo-text';
      default:
        return `text-base-trilhas-tipo-${type}`;
    }
  }

  public onOpenAnnex(annex: any) {
    this._dialog.open<ModalFileViewerComponent>(ModalFileViewerComponent, {
      data: this.formatFile(annex),
    });
  }

  public getPreviousElement() {
    return this.getElement(-1);
  }

  public getNextElement() {
    return this.getElement(1);
  }

  public goToPrevious() {
    const previousElement = this.getPreviousElement();
    if (previousElement) {
      this.setContent(previousElement);
    }
  }

  public goToNext() {
    const nextElement = this.getNextElement();
    if (nextElement) {
      this.setContent(nextElement);
    }
  }

  private getElement(offset: -1 | 1) {
    if (!this.multipleElements || !this.currentElement) {
      return null;
    }

    const currentIndex = findIndex(this.data.trailElements, (element) => element.id === this.currentElement.id);
    if (currentIndex < 0) {
      return null;
    }

    const targetIndex = currentIndex + offset;

    if (targetIndex < 0 || targetIndex >= this.data.trailElements.length) {
      return null;
    }

    return this.data.trailElements[targetIndex];
  }

  private getElementData(element: any) {
    this.elementData$ = this._courseService
      .getElement(element.evento_id, element.trail.slug, element.slug)
      .pipe(safeEmpty());
  }

  private getElementDataWithoutEvent(element: any) {
    this.elementData$ = this._courseService.getElementByVitrine(element.trail.slug, element.slug).pipe(safeEmpty());
  }

  private markElementAsRead(element: any, withEvent: boolean) {
    if (element.tipo === 'avaliacao') {
      return;
    }

    const markAsRead$ = this._store.select(AppSelectors.ActiveUser).pipe(
      take(1),
      filter((user) => !!user.aluno_id),
      switchMap(() =>
        withEvent
          ? this._courseService.markAsRead(element.evento_id, element.trail.slug, element.slug).pipe(
              safeEmpty(),
              switchMap(() => this._dailyService.markElementAsRead(element.slug, this.data.dailyId)),
            )
          : this._dailyService.markElementAsRead(element.slug, this.data.dailyId),
      ),
    );

    markAsRead$.subscribe((res) => {
      if (res.ret === 1) {
        this.data.trailElements = this.data.trailElements.map((te) =>
          te.id === element.id ? { ...te, completed: 1 } : te,
        );
        this._cdrf.markForCheck();
      }
    });
  }

  private formatFile(file: any): File {
    const formattedFile: any = {};
    formattedFile.title = file.nome_arquivo;
    formattedFile.mimetype = file.mimetype;

    switch (file.extensao) {
      case 'xlsx':
      case 'pptx':
      case 'docx':
        formattedFile.type = TypeFile.Office;
        formattedFile.file = file.file;
        break;
      case 'png':
      case 'jpg':
      case 'jpeg':
        formattedFile.type = TypeFile.Image;
        formattedFile.file = file.file;
        break;
      case 'pdf':
        formattedFile.type = TypeFile.Pdf;
        formattedFile.file = file.file;
        break;
      case 'mp4':
      case 'mov':
      case 'm4v':
        formattedFile.type = TypeFile.Video;
        formattedFile.file = file.video;
        formattedFile.vimeo_hash = file.vimeo_hash;
        break;
      case 'mp3':
        formattedFile.type = TypeFile.Audio;
        formattedFile.file = file.file;
        break;
    }

    return formattedFile;
  }
}
